import { ArrowUp, ArrowDown, Minus, Sparkles, Flame, Clock } from "lucide-react";
import { formatCount } from "@/lib/config";

/**
 * Rank movement since the last stored snapshot.
 *
 * Renders nothing until snapshots exist, so a fresh install degrades to the old
 * behaviour rather than showing zeros or invented movement.
 */
export const RankMovement = ({ video, className = "" }) => {
  const { rank_change: change, is_new_on_chart: isNew } = video;

  if (isNew) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 ${className}`}
        title="First time on this chart"
      >
        <Sparkles className="w-2.5 h-2.5" /> NEW
      </span>
    );
  }

  if (change === null || change === undefined) return null;

  if (change === 0) {
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] text-white/40 ${className}`} title="No change">
        <Minus className="w-2.5 h-2.5" />
      </span>
    );
  }

  const climbing = change > 0;
  const Icon = climbing ? ArrowUp : ArrowDown;
  const tone = climbing ? "text-emerald-400" : "text-red-400";

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${tone} ${className}`}
      title={`${climbing ? "Up" : "Down"} ${Math.abs(change)} since last hour`}
    >
      <Icon className="w-2.5 h-2.5" />
      {Math.abs(change)}
    </span>
  );
};

/** Momentum and time-on-chart, both measured rather than estimated. */
export const VelocityBadge = ({ video, className = "" }) => {
  const { views_per_hour: perHour, momentum, hours_on_chart: hours } = video;
  if (!perHour && !hours) return null;

  return (
    <div className={`flex items-center gap-2 text-[10px] text-white/50 ${className}`}>
      {perHour > 0 && (
        <span
          className={`inline-flex items-center gap-1 ${momentum === "rising" ? "text-orange-400" : ""}`}
          title="Views gained per hour, measured between snapshots"
        >
          <Flame className="w-2.5 h-2.5" />
          {formatCount(perHour)}/hr
        </span>
      )}
      {hours > 0 && (
        <span className="inline-flex items-center gap-1" title="How long this has been on the chart">
          <Clock className="w-2.5 h-2.5" />
          {hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`} trending
        </span>
      )}
    </div>
  );
};
